// Retained list semantics, independent of cloud clients.
import { isValid } from 'date-fns';

type ListRecord = Record<string, unknown>;

const asDate = (value: unknown): Date => {
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    return new Date(value);
  }
  return new Date(Number.NaN);
};

export const getListSort =
  (sort: { field?: string; order?: string }) =>
  (_a: ListRecord, _b: ListRecord): number => {
    if (!sort?.field) return 0;
    const a = _a[sort.field];
    const b = _b[sort.field];
    if (!a && !b) return 0;
    if (!a) return sort.order === 'ASC' ? -1 : 1;
    if (!b) return sort.order === 'ASC' ? 1 : -1;

    const parsedDateA = asDate(a);
    const parsedDateB = asDate(b);
    if (isValid(parsedDateA) && isValid(parsedDateB)) {
      return sort.order === 'ASC'
        ? parsedDateA.getTime() - parsedDateB.getTime()
        : parsedDateB.getTime() - parsedDateA.getTime();
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return sort.order === 'ASC' ? a - b : b - a;
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return sort.order === 'ASC'
        ? a.localeCompare(b, undefined, { numeric: true })
        : b.localeCompare(a, undefined, { numeric: true });
    }

    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return sort.order === 'ASC'
        ? Number(a) - Number(b)
        : Number(b) - Number(a);
    }
    return 0;
  };

export const getListFilter =
  (filter: Record<string, unknown>) =>
  (item: ListRecord): boolean => {
    const filterKeys = Object.entries(filter);

    return filterKeys.every(([key, value]) => {
      let _value = value;
      try {
        if (typeof value === 'string') {
          _value = JSON.parse(value);
        }
      } catch {
        // keep original value
      }
      if (value === undefined) {
        return true;
      }
      if (key === 'q') {
        const lq = String(value).trim().toLowerCase();
        if (!lq) return true; // empty q matches everything
        const searchFields = ['name', 'first_name', 'last_name', 'email', 'label', 'notes', 'student_id'];
        return searchFields.some(
          (f) => typeof item[f] === 'string' && item[f].toLowerCase().includes(lq),
        );
      }
      if (key === 'archived_at') {
        return _value === 0 ? !item[key] : !!item[key];
      }
      if (Array.isArray(_value)) {
        return _value.some((v: unknown) => {
          if (v === null && !item[key]) return true;
          return item[key] === v;
        });
      }
      if (_value === null && !item[key]) return true;

      return item[key] === _value;
    });
  };
